import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SlidersHorizontal, Search, MapPin } from 'lucide-react';
import { Heading, Text, Stack, Container } from '@/components/ui/index.js';
import { BottomNav } from '@/components/navigation/index.js';
import { useEventsFilterStore } from '@/features/events/store/useEventsFilterStore.js';
import { EventCard, FilterSheet, FilterPill, ViewToggle } from './components/index.js';
import styles from './EventsPage.module.css';

// Mock events data
const mockEvents = [
  {
    id: 1,
    title: 'Weekend Basketball Pickup',
    date: 'Dec 16, 2023',
    time: '2:00 PM',
    location: 'Central Park Court',
    sport: 'basketball',
    skillLevel: 'intermediate',
    participants: 8,
    maxParticipants: 12,
    isLive: false,
    distance: 0.5,
  },
  {
    id: 2,
    title: 'Morning Running Group',
    date: 'Dec 17, 2023',
    time: '7:00 AM',
    location: 'Riverside Trail',
    sport: 'running',
    skillLevel: 'beginner',
    participants: 15,
    maxParticipants: 20,
    isLive: true,
    distance: 1.2,
  },
  {
    id: 3,
    title: 'Advanced Tennis Clinic',
    date: 'Dec 16, 2023',
    time: '4:00 PM',
    location: 'Tennis Academy',
    sport: 'tennis',
    skillLevel: 'advanced',
    participants: 4,
    maxParticipants: 6,
    isLive: false,
    distance: 2.5,
  },
  {
    id: 4,
    title: 'Sunset Yoga Flow',
    date: 'Dec 16, 2023',
    time: '6:00 PM',
    location: 'Beachfront Park',
    sport: 'yoga',
    skillLevel: 'beginner',
    participants: 12,
    maxParticipants: 15,
    isLive: false,
    distance: 0.8,
  },
  {
    id: 5,
    title: 'Soccer Match - Open Play',
    date: 'Dec 17, 2023',
    time: '10:00 AM',
    location: 'Community Fields',
    sport: 'soccer',
    skillLevel: 'intermediate',
    participants: 18,
    maxParticipants: 22,
    isLive: false,
    distance: 1.5,
  },
  {
    id: 6,
    title: 'Indoor Swimming Session',
    date: 'Dec 18, 2023',
    time: '6:00 AM',
    location: 'Aquatic Center',
    sport: 'swimming',
    skillLevel: 'intermediate',
    participants: 6,
    maxParticipants: 8,
    isLive: false,
    distance: 3.2,
  },
  {
    id: 7,
    title: 'Mountain Bike Trail Ride',
    date: 'Dec 17, 2023',
    time: '9:00 AM',
    location: 'Trailhead Park',
    sport: 'cycling',
    skillLevel: 'advanced',
    participants: 5,
    maxParticipants: 10,
    isLive: false,
    distance: 4.8,
  },
  {
    id: 8,
    title: 'Beach Volleyball Tournament',
    date: 'Dec 16, 2023',
    time: '3:00 PM',
    location: 'Sunset Beach',
    sport: 'volleyball',
    skillLevel: 'intermediate',
    participants: 10,
    maxParticipants: 12,
    isLive: true,
    distance: 2.1,
  },
];

// Filter label mappings
const filterLabelMap = {
  sport: {
    basketball: 'Basketball',
    soccer: 'Soccer',
    tennis: 'Tennis',
    running: 'Running',
    swimming: 'Swimming',
    cycling: 'Cycling',
    volleyball: 'Volleyball',
    yoga: 'Yoga',
  },
  skillLevel: {
    beginner: 'Beginner',
    intermediate: 'Intermediate',
    advanced: 'Advanced',
  },
  dateRange: {
    today: 'Today',
    'this-week': 'This Week',
    'this-weekend': 'This Weekend',
    'next-week': 'Next Week',
  },
  timeOfDay: {
    morning: 'Morning',
    afternoon: 'Afternoon',
    evening: 'Evening',
  },
};

/**
 * EventsPage - Events Browser with grid/list toggle, filters, and event cards
 * 
 * Features:
 * - Grid/List view toggle
 * - Filter sheet for sport, date, skill level, distance, time
 * - Active filter pills with remove functionality
 * - Glassmorphic event cards with join CTA
 * - Live event indicators
 * - Bottom navigation integration
 */
export function EventsPage() {
  const navigate = useNavigate();
  
  // Store state
  const {
    viewMode,
    setViewMode,
    filters,
    isFilterSheetOpen,
    openFilterSheet,
    closeFilterSheet,
    toggleSportFilter,
    toggleSkillLevelFilter,
    setDateRangeFilter,
    setMaxDistanceFilter,
    toggleTimeOfDayFilter,
    removeFilter,
    clearAllFilters,
    hasActiveFilters,
    getActiveFilterCount,
  } = useEventsFilterStore();

  // Local state
  const [joinedEvents, setJoinedEvents] = useState(new Set());

  // Filter events
  const filteredEvents = useMemo(() => {
    return mockEvents.filter((event) => {
      // Sport filter
      if (filters.sports.length > 0 && !filters.sports.includes(event.sport)) {
        return false;
      }
      
      // Skill level filter
      if (filters.skillLevels.length > 0 && !filters.skillLevels.includes(event.skillLevel)) {
        return false;
      }
      
      // Distance filter
      if (event.distance > filters.maxDistance) {
        return false;
      }
      
      return true;
    });
  }, [filters]);

  // Active filter pills
  const activeFilterPills = useMemo(() => {
    const pills = [];
    
    filters.sports.forEach((sport) => {
      pills.push({ type: 'sport', value: sport, label: filterLabelMap.sport[sport] || sport });
    });
    
    filters.skillLevels.forEach((level) => {
      pills.push({ type: 'skillLevel', value: level, label: filterLabelMap.skillLevel[level] });
    });
    
    if (filters.dateRange) {
      pills.push({ type: 'dateRange', value: filters.dateRange, label: filterLabelMap.dateRange[filters.dateRange] });
    }
    
    filters.timeOfDay.forEach((time) => {
      pills.push({ type: 'timeOfDay', value: time, label: filterLabelMap.timeOfDay[time] });
    });
    
    if (filters.maxDistance !== 10) {
      pills.push({ type: 'maxDistance', value: String(filters.maxDistance), label: `Within ${filters.maxDistance}mi` });
    }
    
    return pills;
  }, [filters]);

  // Handlers
  const handleJoinEvent = (eventId) => {
    setJoinedEvents((prev) => new Set([...prev, eventId]));
  };

  const handleRemoveFilter = (type, value) => {
    removeFilter(type, value);
  };

  const handleApplyFilters = () => {
    closeFilterSheet();
  };

  return (
    <div className={styles.page}>
      {/* Main content */}
      <main className={styles.main}>
        <Container size="full" padding className={styles.container}>
          {/* Header */}
          <header className={styles.header}>
            <div className={styles.headerTop}>
              <Stack direction="row" gap={2} align="center">
                <MapPin size={24} className={styles.locationIcon} />
                <div>
                  <Text size="xs" color="muted">Location</Text>
                  <Text size="sm" color="primary" weight="medium">San Francisco, CA</Text>
                </div>
              </Stack>
            </div>

            <div className={styles.headerControls}>
              <Heading level={1} size="2xl" color="primary" className={styles.pageTitle}>
                Events
              </Heading>
              
              <Stack direction="row" gap={2} align="center">
                <ViewToggle viewMode={viewMode} onViewChange={setViewMode} />
                <button
                  type="button"
                  className={styles.filterButton}
                  onClick={openFilterSheet}
                  aria-label="Open filters"
                >
                  <SlidersHorizontal size={20} />
                  {hasActiveFilters() && (
                    <span className={styles.filterBadge}>
                      {getActiveFilterCount()}
                    </span>
                  )}
                </button>
              </Stack>
            </div>

            {/* Search bar */}
            <div className={styles.searchBar}>
              <Search size={18} className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search events..."
                className={styles.searchInput}
              />
            </div>
          </header>

          {/* Active filters */}
          {hasActiveFilters() && (
            <div className={styles.activeFilters}>
              <div className={styles.filterPills}>
                {activeFilterPills.map((pill) => (
                  <FilterPill
                    key={`${pill.type}-${pill.value}`}
                    type={pill.type}
                    value={pill.value}
                    label={pill.label}
                    onRemove={handleRemoveFilter}
                  />
                ))}
              </div>
              <button
                type="button"
                className={styles.clearAllButton}
                onClick={clearAllFilters}
              >
                Clear All
              </button>
            </div>
          )}

          {/* Events grid/list */}
          <section className={styles.eventsSection}>
            <Stack direction="row" gap={2} align="center" justify="space-between" className={styles.resultsHeader}>
              <Text size="sm" color="muted">
                {filteredEvents.length} {filteredEvents.length === 1 ? 'event' : 'events'} found
              </Text>
            </Stack>

            {filteredEvents.length > 0 ? (
              <div className={`${styles.eventsContainer} ${styles[viewMode]}`}>
                {filteredEvents.map((event, index) => (
                  <EventCard
                    key={event.id}
                    event={{ ...event, animationDelay: index * 100 }}
                    viewMode={viewMode}
                    onJoin={handleJoinEvent}
                  />
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>🔍</div>
                <Heading level={2} size="lg" color="primary" align="center">
                  No events found
                </Heading>
                <Text size="base" color="secondary" align="center" className={styles.emptyText}>
                  Try adjusting your filters to see more events in your area.
                </Text>
                <button
                  type="button"
                  className={styles.adjustFiltersButton}
                  onClick={openFilterSheet}
                >
                  Adjust Filters
                </button>
              </div>
            )}
          </section>
        </Container>
      </main>

      {/* Filter Sheet */}
      <FilterSheet
        isOpen={isFilterSheetOpen}
        onClose={closeFilterSheet}
        filters={filters}
        onToggleSport={toggleSportFilter}
        onToggleSkillLevel={toggleSkillLevelFilter}
        onSetDateRange={setDateRangeFilter}
        onSetMaxDistance={setMaxDistanceFilter}
        onToggleTimeOfDay={toggleTimeOfDayFilter}
        onClearAll={clearAllFilters}
        onApply={handleApplyFilters}
      />

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}

export default EventsPage;
